// src/views/BackOffice/ManagePropertyApplications.js
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardBody, 
  CardTitle, 
  Badge, 
  Row, 
  Col, 
  Button, 
  Table,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Alert,
  Spinner
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/Axios';

const ManagePropertyApplications = () => {
  const navigate = useNavigate();
  
  // State management
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ type: '', message: '', visible: false });

  // Fetch all properties with 'created' status from all providers
  useEffect(() => {
    const fetchCreatedProperties = async () => {
      try {
        setLoading(true);
        // Fetch all properties with created status for back office review
        const response = await axiosInstance.get('/api/properties/all-created/');
        
        if (response.status === 200) {
          setProperties(response.data.properties || []);
        }
      } catch (error) {
        console.error('Error fetching created properties:', error);
        setAlert({
          type: 'danger',
          message: 'Failed to load property applications. Please try again.',
          visible: true
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCreatedProperties();
  }, []);

  const handleOwnership = (propertyId) => {
    navigate(`/backoffice-dashboard/property-ownership-review/${propertyId}`);
  };

  const handleOperationalStandards = (propertyId) => {
    navigate(`/backoffice-dashboard/property-operational-review/${propertyId}`);
  };

  const handlePoliciesInsurance = (propertyId) => {
    navigate(`/backoffice-dashboard/property-policies-review/${propertyId}`);
  };

  const handleBookInspection = (propertyId) => {
    navigate(`/backoffice-dashboard/property-book-inspection/${propertyId}`);
  };

  const handleProviderDetails = (property) => {
    // Navigate to provider details based on provider type and ID
    if (property.provider_info) {
      navigate(`/backoffice-dashboard/ManageProviders/${property.provider_info.id}`);
    }
  };

  const formatAddress = (property) => {
    const parts = [
      property.addresslines,
      property.suburb,
      property.city,
      property.province
    ].filter(Boolean);
    return parts.join(', ');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'created':
        return <Badge color="secondary">Awaiting Review</Badge>;
      case 'inspection_requested':
        return <Badge color="warning">Inspection Requested</Badge>;
      case 'inspection_booked':
        return <Badge color="info">Inspection Booked</Badge>;
      case 'inspection_failed':
        return <Badge color="danger">Inspection Failed</Badge>;
      case 'inspection_passed':
        return <Badge color="success">Inspection Passed</Badge>;
      case 'active':
        return <Badge color="success">Active</Badge>;
      case 'inactive':
        return <Badge color="secondary">Inactive</Badge>;
      case 'suspended':
        return <Badge color="danger">Suspended</Badge>;
      default:
        return <Badge color="light">{status}</Badge>;
    }
  };

  const getProviderDisplayName = (provider) => {
    if (!provider) return 'Unknown Provider';
    
    if (provider.provider_type === 'entity') {
      return provider.company_name || 'N/A';
    }
    return `${provider.first_name || 'N/A'} ${provider.last_name || ''}`.trim() || 'N/A';
  };

  const getProviderType = (provider) => {
    if (!provider) return 'Unknown';
    return provider.provider_type === 'entity' ? 'Entity' : 'Individual';
  };

  return (
    <div className="container-fluid py-4">
      {/* Header Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 className="mb-1">
                <i className="bi bi-building me-2 text-primary"></i>
                Property Applications & Document Review
              </h2>
              <p className="text-muted mb-1">
                Review and manage property applications submitted by providers
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Alert */}
      {alert.visible && (
        <Row className="mb-3">
          <Col>
            <Alert 
              color={alert.type} 
              toggle={() => setAlert({ ...alert, visible: false })}
            >
              {alert.message}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Properties Table */}
      <Row>
        <Col md="12">
          <Card>
            <CardBody>
              <CardTitle tag="h4" className="mb-4">
                <i className="bi bi-clipboard-check me-2 text-primary"></i>
                Property Applications Awaiting Review
                {!loading && properties.length > 0 && (
                  <Badge color="primary" className="ms-2">
                    {properties.length}
                  </Badge>
                )}
              </CardTitle>
              
              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-3 text-muted">Loading property applications...</p>
                </div>
              ) : (
                <>
                  {properties.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="bi bi-check-circle display-4 text-success"></i>
                      <h5 className="mt-3 text-muted">No Pending Applications</h5>
                      <p className="text-muted">
                        All property applications have been reviewed. New applications will appear here.
                      </p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table hover>
                        <thead className="table-light">
                          <tr>
                            <th>Property Details</th>
                            <th>Provider</th>
                            <th>Provider Type</th>
                            <th>Contact</th>
                            <th>Address</th>
                            <th>Capacity</th>
                            <th>Status</th>
                            <th>Provider Actions</th>
                            <th className="text-center">Review Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {properties.map((property) => (
                            <tr key={property.id}>
                              <td>
                                <div className="fw-bold text-primary">
                                  {property.trading_name || `Property #${property.id}`}
                                </div>
                                <small className="text-muted">
                                  ID: {property.id}
                                </small>
                              </td>
                              <td>
                                <div className="fw-bold">
                                  {getProviderDisplayName(property.provider_info)}
                                </div>
                                {property.provider_info && property.provider_info.provider_type === 'entity' && (
                                  <small className="text-muted">
                                    Rep: {property.provider_info.first_name} {property.provider_info.last_name}
                                  </small>
                                )}
                              </td>
                              <td>
                                <Badge color={property.provider_info?.provider_type === 'entity' ? 'info' : 'success'}>
                                  {getProviderType(property.provider_info)}
                                </Badge>
                              </td>
                              <td>
                                <div>{property.provider_info?.email || 'N/A'}</div>
                                <small className="text-muted">
                                  {property.provider_info?.contact_phone || 'No phone'}
                                </small>
                              </td>
                              <td>
                                <div>{formatAddress(property)}</div>
                                <small className="text-muted">
                                  {property.postcode}
                                </small>
                              </td>
                              <td className="text-center">
                                <div>
                                  <span className="badge bg-light text-dark me-1">
                                    {property.number_of_rooms || 0} rooms
                                  </span>
                                </div>
                                <div>
                                  <span className="badge bg-light text-dark">
                                    {property.number_of_beds || 0} beds
                                  </span>
                                </div>
                              </td>
                              <td>
                                {getStatusBadge(property.status)}
                              </td>
                              <td className="text-center">
                                <Button
                                  onClick={() => handleProviderDetails(property)}
                                  color="info"
                                  size="sm"
                                  className="btn-sm"
                                >
                                  <i className="bi bi-person-circle me-1"></i>
                                  Provider Details
                                </Button>
                              </td>
                              <td className="text-center">
                                <UncontrolledDropdown>
                                  <DropdownToggle 
                                    color="primary" 
                                    size="sm" 
                                    className="btn-outline-primary"
                                    caret={false}
                                  >
                                    <i className="bi bi-three-dots"></i>
                                  </DropdownToggle>
                                  <DropdownMenu end>
                                    <DropdownItem 
                                      onClick={() => handleOwnership(property.id)}
                                    >
                                      <i className="bi bi-file-earmark-text me-2"></i>
                                      Review Ownership & Zoning
                                    </DropdownItem>
                                    <DropdownItem 
                                      onClick={() => handleOperationalStandards(property.id)}
                                    >
                                      <i className="bi bi-shield-check me-2"></i>
                                      Review Operational Standards
                                    </DropdownItem>
                                    <DropdownItem 
                                      onClick={() => handlePoliciesInsurance(property.id)}
                                    >
                                      <i className="bi bi-clipboard-data me-2"></i>
                                      Review Policies & Insurance
                                    </DropdownItem>
                                    <DropdownItem divider />
                                    <DropdownItem 
                                      onClick={() => handleBookInspection(property.id)}
                                      className="text-success"
                                    >
                                      <i className="bi bi-calendar-check me-2"></i>
                                      Schedule Inspection
                                    </DropdownItem>
                                  </DropdownMenu>
                                </UncontrolledDropdown>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ManagePropertyApplications;